import { FileOpener } from '@awesome-cordova-plugins/file-opener';
import { Filesystem, Directory } from '@capacitor/filesystem';
import { IonIcon, IonModal, isPlatform, useIonToast } from '@ionic/react';
import { eyeOutline } from 'ionicons/icons';
import React, { PropsWithChildren, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useInvoices } from '../../../hooks/InvoiceHooks';
import { Invoice } from '../../../model/invoices/invoices';
import { CloseButton } from '../../globals/CloseButton';
import { useLogger } from '../../../services/LogService';

export type InvoiceContentProps = PropsWithChildren<{
  invoice: Invoice;
  showModal: boolean;
  setShowModal: any;
  invoiceDate: string;
}>;

export const InvoiceModal: React.FC<InvoiceContentProps> = ({
  invoice,
  showModal,
  setShowModal,
  invoiceDate,
  children,
}) => {
  const { invoiceService } = useInvoices();
  const [present] = useIonToast();
  const intl = useIntl();
  const [width] = useState(window.innerWidth);

  const logger = useLogger('InvoiceModal');

  async function openFile(base64: any, name: string, mime: string) {
    logger.info(`openFile() name='${name}', dir: '${Directory.Cache}'`);
    Filesystem.writeFile({
      path: 'invoices/' + name,
      directory: Directory.Cache,
      data: base64,
      recursive: true,
    })
      .then((res) => {
        const pathname = decodeURIComponent(new URL(res.uri).pathname);
        logger.info(`URI = '${res.uri}', pathname = '${pathname}'`);
        FileOpener.open(pathname, mime).catch((e) => {
          logger.error(`Error opening pdf file ${pathname}`, e);
        });
      })
      .catch(() => {
        console.error('Error writing pdf file');
      });
    console.log('End of loading');
  }

  const handeDownload = (id: string) => {
    invoiceService
      .getBase64File(invoice.link)
      .then(({ base64, filename, mime }) => {
        if (base64) {
          if (isPlatform('capacitor')) {
            return openFile(base64, filename, mime);
          } else {
            const link = document.createElement('a');
            link.href = 'data:' + mime + ';base64,' + base64;
            document.body.appendChild(link);
            link.setAttribute('download', filename);
            link.click();
            document.body.removeChild(link);
          }
        } else {
          present({
            cssClass: 'text-center',
            color: 'dark',
            position: 'top',
            message: intl.formatMessage({
              id: 'File does not exist.',
            }),
            duration: 2000,
          });
        }
      });
  };

  return width > 768 ? (
    <div className="modal w-full">
      <IonModal
        isOpen={showModal}
        cssClass="directory-modal"
        swipeToClose={true}
        animated={true}
        showBackdrop={true}
        backdropDismiss={true}
        onDidDismiss={() => setShowModal(false)}
      >
        <div
          className={`hidden md:block modal-cross z-50 absolute top-6 left-6 md:top-9 md:left-9`}
        >
          <CloseButton
            to="/services/invoices"
            onClick={() => setShowModal(false)}
          />
        </div>

        <div className="bg-white h-full md:h-auto py-6 px-6 rounded-t-3xl md:rounded-3xl">
          <div className="grid grid-cols-2 items-center">
            <h3 className=" text-black font-medium text-lg px-6 text-left">
              <FormattedMessage id="Invoice" />
              {' #' +
                invoice.number +
                ' - ' +
                invoiceDate +
                ' ' +
                invoice.issueDate?.split('-')[0]}
            </h3>
            <button
              type="button"
              className="flex items-center justify-self-end w-max px-3 py-1 border border-transparent shadow-sm font-medium rounded-3xl text-sm text-white text-center bg-primary hover:bg-secondary focus:outline-none disabled:bg-lightGrey"
              onClick={() => handeDownload(invoice.id)}
            >
              <p>
                <FormattedMessage id="See main invoice" />
              </p>
            </button>
          </div>

          <div className="flex-1 flex flex-col justify-between items-center px-6 pb-5">
            {invoice?.details.length > 0 ? (
              <p className="text-gray-400	font-sm text-sm mt-4 text-left w-full">
                <FormattedMessage id="Additional parts" />
              </p>
            ) : null}

            {invoice?.details.map((ticket, index) => {
              return (
                <div
                  key={index}
                  className="flex justify-between items-center w-full py-4 border-b
                  border-gray-100 bg-white"
                >
                  <h4 className="text-black font-bold">
                    {'Ticket #' + ticket.number + ' - ' + ticket.title}
                  </h4>

                  <button
                    onClick={() => handeDownload(ticket.id)}
                    className="py-2  text-sm text-primary"
                  >
                    <div className="h-full flex items-center md:flex md:flex-col">
                      <p className="w-full font-semibold">
                        <FormattedMessage id="See the invoice" />
                      </p>
                    </div>
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      </IonModal>
      {children}
    </div>
  ) : (
    <div className="modal w-full">
      <IonModal
        isOpen={showModal}
        cssClass="directory-modal invoices-modal"
        swipeToClose={true}
        animated={true}
        showBackdrop={true}
        backdropDismiss={true}
        onDidDismiss={() => setShowModal(false)}
      >
        <div
          className={`hidden md:block modal-cross z-50 absolute top-6 left-6 md:top-9 md:left-9`}
        >
          <CloseButton
            to="/services/invoices"
            onClick={() => setShowModal(false)}
          />
        </div>

        <div className="bg-black h-full md:h-auto py-6 px-6 rounded-t-3xl md:rounded-3xl">
          <h3 className=" text-white font-medium text-lg mt-6 pb-0 text-left">
            <FormattedMessage id="Invoice" />
            {' #' +
              invoice.number +
              ' - ' +
              invoiceDate +
              ' ' +
              invoice.issueDate?.split('-')[0]}
          </h3>

          <div className="flex-1 flex flex-col justify-between items-center mb-5">
            {invoice?.details.length > 0 ? (
              <p className="text-gray-400	font-sm text-sm mt-6 text-left w-full">
                <FormattedMessage id="Additional parts" />
              </p>
            ) : null}

            {invoice?.details.map((ticket, index) => {
              return (
                <div
                  key={index}
                  className="flex justify-between items-center w-full py-4 border-b
                    border-gray-300 bg-black text-white"
                >
                  <h4 className="font-medium">
                    {' '}
                    {'Ticket #' + ticket.number + ' - ' + ticket.title}
                  </h4>

                  <button
                    onClick={() => handeDownload(ticket.id)}
                    className="bg-primary rounded-3xl px-2 py-2 flex justify-center items-centerhite w-7 h-7 items-center text-white"
                  >
                    <div className="h-full flex items-center md:flex md:flex-col">
                      <IonIcon icon={eyeOutline}></IonIcon>
                    </div>
                  </button>
                </div>
              );
            })}

            <li className="col-span-1 flex flex-col text-left bg-black rounded-xl my-3 w-full">
              <button
                type="button"
                className="inline-flex items-center w-full justify-center px-6 py-4 mt-3 mb-3 border border-transparent shadow-sm font-bold rounded-3xl text-sm text-white text-center bg-primary hover:bg-secondary focus:outline-none disabled:bg-lightGrey"
                onClick={() => handeDownload(invoice.id)}
              >
                <p>
                  <FormattedMessage id="See main invoice" />
                </p>
              </button>
            </li>
          </div>
        </div>
      </IonModal>
      {children}
    </div>
  );
};
export default InvoiceModal;
